import React from 'react';
import { useTranslation } from 'localization';
import type { RosterCoverage } from 'types/spherePoints';

export interface RollupRosterCoverageProps {
  coverage: RosterCoverage;
  hiddenCount: number;
}

/** "X of Y reporting". Y is `coverage.total + hiddenCount` (D70): the server's total excludes hidden members. */
const RollupRosterCoverage: React.FC<RollupRosterCoverageProps> = ({ coverage, hiddenCount }) => {
  const { t } = useTranslation();
  const displayTotal = coverage.total + hiddenCount;
  const hiddenTooltip = hiddenCount > 0 ? t('statusFactors.roster.coverageHidden', '{{count}} hidden', { count: hiddenCount }) : undefined;
  return (
    <span className="rollup-roster__coverage" data-testid="roster-coverage" title={hiddenTooltip}>
      {t('statusFactors.roster.coverageLine', '{{reporting}} of {{total}} reporting', {
        reporting: coverage.reporting,
        total: displayTotal,
      })}
    </span>
  );
};

export interface RollupRosterHiddenNoteProps {
  hiddenCount: number;
}

/** Footnote for members the viewer cannot see but whose status still counts toward the rollup. */
export const RollupRosterHiddenNote: React.FC<RollupRosterHiddenNoteProps> = ({ hiddenCount }) => {
  const { t } = useTranslation();
  return (
    <p className="rollup-roster__hidden-note" data-testid="roster-hidden-note">
      <i className="pi pi-eye-slash" aria-hidden="true" />
      <span>
        {t(
          'statusFactors.roster.coverage.hiddenNote',
          hiddenCount === 1
            ? "{{count}} member you can't see is included in this rollup"
            : "{{count}} members you can't see are included in this rollup",
          { count: hiddenCount }
        )}
      </span>
    </p>
  );
};

export default RollupRosterCoverage;
